import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import ActionDropdown from '../../../components/common/action-dropdown';
import { Plus, Filter, Users, DollarSign, TrendingUp } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';
import RiskScoreBadge from '../../../components/trading/RiskScoreBadge';

type Manager = {
  id: string;
  name: string;
  email: string;
  totalFollowers: number;
  totalCopied: number;
  totalProfit: number;
  roi: number;
  riskLevel: 'low' | 'medium' | 'high';
  riskScore: number;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  commissionRate: number;
};

const CopyTradingManagers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const managers: Manager[] = [
    {
      id: 'TRD001',
      name: 'John Smith',
      email: 'john@example.com',
      totalFollowers: 45,
      totalCopied: 125000,
      totalProfit: 53125,
      roi: 42.5,
      riskLevel: 'medium',
      riskScore: 5,
      status: 'active',
      createdAt: '2024-01-15',
      commissionRate: 15,
    },
    {
      id: 'TRD002',
      name: 'Jane Doe',
      email: 'jane@example.com',
      totalFollowers: 38,
      totalCopied: 98000,
      totalProfit: 35084,
      roi: 35.8,
      riskLevel: 'low',
      riskScore: 3,
      status: 'active',
      createdAt: '2024-01-10',
      commissionRate: 12,
    },
    {
      id: 'TRD003',
      name: 'Bob Johnson',
      email: 'bob@example.com',
      totalFollowers: 22,
      totalCopied: 75000,
      totalProfit: 13650,
      roi: 18.2,
      riskLevel: 'high',
      riskScore: 7,
      status: 'pending',
      createdAt: '2024-01-08',
      commissionRate: 18,
    },
  ];

  const columns: DataTableColumn<Manager>[] = [
    {
      id: 'id',
      header: 'Trader ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: row => row.email,
      sortable: true,
      sortValue: row => row.email,
      width: '220px',
    },
    {
      id: 'totalFollowers',
      header: 'Followers',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <span>{row.totalFollowers}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.totalFollowers,
      width: '120px',
    },
    {
      id: 'totalCopied',
      header: 'Total Copied',
      accessor: row => `$${row.totalCopied.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalCopied,
      width: '140px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'riskLevel',
      header: 'Risk',
      accessor: row => <RiskScoreBadge score={row.riskScore} />,
      sortable: true,
      sortValue: row => row.riskScore,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View trader:', row.id)}
          onEdit={() => console.log('Edit trader:', row.id)}
          onDelete={() => console.log('Delete trader:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const filteredManagers = managers.filter(manager => {
    const matchesSearch =
      searchTerm === '' ||
      manager.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      manager.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      manager.id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const totalProfit = managers.reduce((sum, mgr) => sum + mgr.totalProfit, 0);
  const totalCopied = managers.reduce((sum, mgr) => sum + mgr.totalCopied, 0);
  const activeManagers = managers.filter(mgr => mgr.status === 'active').length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Manager Management"
          description="Manage copy trading master traders"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <Users className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Traders</p>
            <p className="text-2xl font-bold text-blue-600">
              {managers.length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <Users className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Active Traders</p>
            <p className="text-2xl font-bold text-green-600">
              {activeManagers}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg">
          <DollarSign className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Copied</p>
            <p className="text-2xl font-bold text-purple-600">
              ${totalCopied.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg">
          <TrendingUp className="h-5 w-5 text-orange-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Profit</p>
            <p className="text-2xl font-bold text-orange-600">
              ${totalProfit.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Trader
          </Button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <Card>
          <CardHeader>
            <CardTitle>All Traders</CardTitle>
            <CardDescription>
              Manage and monitor all copy trading master traders
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={filteredManagers}
              searchPlaceholder="Search by name, email, or trader ID..."
              onSearchChange={setSearchTerm}
              showColumnVisibility={true}
              showExportButton={true}
              showAddButton={false}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredManagers.map(manager => (
            <Card
              key={manager.id}
              className="hover:shadow-lg transition-shadow"
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{manager.name}</CardTitle>
                    <CardDescription>{manager.email}</CardDescription>
                  </div>
                  <TradingStatusBadge status={manager.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Followers</p>
                    <p className="text-lg font-semibold">
                      {manager.totalFollowers}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Copied</p>
                    <p className="text-lg font-semibold">
                      ${manager.totalCopied.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">ROI</p>
                    <p className="text-lg font-semibold text-green-600">
                      {manager.roi.toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Commission</p>
                    <p className="text-lg font-semibold">
                      {manager.commissionRate}%
                    </p>
                  </div>
                </div>
                <RiskScoreBadge score={manager.riskScore} />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    View Details
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CopyTradingManagers;
